import React, { useEffect } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../src/localization/i18n.ts";
import EmailVerificationResult from "./pages/auth/EmailVerificationResult";
import AppStoreScreenshots from "./pages/internal/AppStoreScreenshots";
import LandingCardRecorder from "./pages/internal/LandingCardRecorder";
import StyleGuide from "./pages/internal/StyleGuide";
import DesignPlayground from "./pages/internal/DesignPlayground";
import YouWoConsumerLanding from "./pages/internal/YouWoConsumerLanding";
import DownloadDesktop from "./pages/internal/DownloadDesktop";
import CompetitorAnalysis from "./pages/internal/CompetitorAnalysis";
import InternalDashboard from "./pages/internal/InternalDashboard";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import ResetPasswordEmail from "./pages/auth/ResetPasswordEmail";
import ResetPasswordSuccess from "./pages/auth/ResetPasswordSuccess";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import VerifyEmail from "./pages/auth/VerifyEmail";
import DataDeletion from "./pages/DataDeletion";
import LandingPage from "./pages/LandingPage";
import ModernLandingPage from "./pages/ModernLandingPage";
import PricingPage from "./pages/PricingPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Support from "./pages/SupportPage";
import YouWoVsChatGPTForStudying from "./pages/compare/YouWoVsChatGPTForStudying";
// Free AI Tools Pages
import {
  AllTools,
  YouTubeSummarizer,
  PDFSummarizer,
  AudioTranscriber,
  FlashcardMaker,
  QuizGenerator,
  MeetingNotes,
  LectureNotes,
  // SEO Landing Pages
  QuizletAlternative,
  AnkiAlternative,
  NotabilityAlternative,
  OtterAIAlternative,
  ChatGPTForStudents,
  AIStudyTools,
  AIFlashcardGenerator,
  LectureTranscription,
  PDFToFlashcards,
  PDFToStudyGuide,
  AINoteTaking,
} from "./pages/tools";
import { injectStore } from "./redux/api";
import { fetchUser } from "./redux/features/user/userSlice";
import store, { AppDispatch, RootState } from "./redux/store";

// Protected route for internal tools - requires ?key=youwoai2026
const ProtectedInternalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");
  const isDev = process.env.NODE_ENV === "development";

  // Allow access in dev mode OR with correct key
  if (isDev || key === "youwoai2026") {
    return <>{children}</>;
  }

  // Redirect to landing page if not authorized
  return <Navigate to="/" replace />;
};

function AppContent() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const userRole = useSelector((state: RootState) => state.user.user?.role);
  const user = useSelector((state: RootState) => state.user.user);
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const dispatch: AppDispatch = useDispatch();
  const { i18n } = useTranslation();

  // Sync Redux language with i18n on mount
  useEffect(() => {
    if (currentLanguage && i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    console.log("isLoggedIn", isLoggedIn);
    if (isLoggedIn) {
      dispatch(fetchUser());
    }
  }, [isLoggedIn, dispatch, userRole]);

  return (
    <Routes>
      {!isLoggedIn ? (
        <>
          <Route path="/" element={<YouWoConsumerLanding />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/landing-page" element={<ModernLandingPage />} />
          <Route path="/old-landing" element={<LandingPage />} />
          <Route path="/modern-landing" element={<ModernLandingPage />} />
          <Route path="/support" element={<Support />} />
          {/* Free AI Tools Routes */}
          <Route path="/tools" element={<AllTools />} />
          <Route path="/tools/youtube-summarizer" element={<YouTubeSummarizer />} />
          <Route path="/tools/pdf-summarizer" element={<PDFSummarizer />} />
          <Route path="/tools/audio-transcriber" element={<AudioTranscriber />} />
          <Route path="/tools/flashcard-maker" element={<FlashcardMaker />} />
          <Route path="/tools/quiz-generator" element={<QuizGenerator />} />
          <Route path="/tools/meeting-notes" element={<MeetingNotes />} />
          <Route path="/tools/lecture-notes" element={<LectureNotes />} />
          {/* SEO Landing Pages */}
          <Route path="/tools/quizlet-alternative" element={<QuizletAlternative />} />
          <Route path="/tools/anki-alternative" element={<AnkiAlternative />} />
          <Route path="/tools/notability-alternative" element={<NotabilityAlternative />} />
          <Route path="/tools/otter-ai-alternative" element={<OtterAIAlternative />} />
          <Route path="/tools/chatgpt-for-students" element={<ChatGPTForStudents />} />
          <Route path="/tools/ai-study-tools" element={<AIStudyTools />} />
          <Route path="/tools/ai-flashcard-generator" element={<AIFlashcardGenerator />} />
          <Route path="/tools/lecture-transcription" element={<LectureTranscription />} />
          <Route path="/tools/pdf-to-flashcards" element={<PDFToFlashcards />} />
          <Route path="/tools/pdf-to-study-guide" element={<PDFToStudyGuide />} />
          <Route path="/tools/ai-note-taking" element={<AINoteTaking />} />
          <Route path="/compare/youwo-vs-chatgpt-for-studying" element={<YouWoVsChatGPTForStudying />} />
          {/* Internal tools - protected by query param */}
          <Route path="/internal/screenshots" element={<ProtectedInternalRoute><AppStoreScreenshots /></ProtectedInternalRoute>} />
          <Route path="/internal/landing-cards" element={<ProtectedInternalRoute><LandingCardRecorder /></ProtectedInternalRoute>} />
          <Route path="/internal/style-guide" element={<ProtectedInternalRoute><StyleGuide /></ProtectedInternalRoute>} />
          <Route path="/internal/design-playground" element={<ProtectedInternalRoute><DesignPlayground /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-consumer-landing" element={<ProtectedInternalRoute><YouWoConsumerLanding /></ProtectedInternalRoute>} />
          <Route path="/internal/download" element={<ProtectedInternalRoute><DownloadDesktop /></ProtectedInternalRoute>} />
          <Route path="/internal/competitors" element={<ProtectedInternalRoute><CompetitorAnalysis /></ProtectedInternalRoute>} />
          <Route path="/internal/dashboard" element={<ProtectedInternalRoute><InternalDashboard /></ProtectedInternalRoute>} />
          {/* <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} /> */}
          {/* <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} /> */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/reset-password-success"
            element={<ResetPasswordSuccess />}
          />
          <Route
            path="/email-verification"
            element={<EmailVerificationResult />}
          />
          {/* <Route
            path="/reset-password-email"
            element={<ResetPasswordEmail />}
          /> */}
          <Route path="*" element={<Navigate to="landing-page" />} />
        </>
      ) : (
        // temp holder
        <>
          <Route path="/" element={<YouWoConsumerLanding />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/landing-page" element={<ModernLandingPage />} />
          <Route path="/old-landing" element={<LandingPage />} />
          <Route path="/modern-landing" element={<ModernLandingPage />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/support" element={<Support />} />
          {/* Free AI Tools Routes */}
          <Route path="/tools" element={<AllTools />} />
          <Route path="/tools/youtube-summarizer" element={<YouTubeSummarizer />} />
          <Route path="/tools/pdf-summarizer" element={<PDFSummarizer />} />
          <Route path="/tools/audio-transcriber" element={<AudioTranscriber />} />
          <Route path="/tools/flashcard-maker" element={<FlashcardMaker />} />
          <Route path="/tools/quiz-generator" element={<QuizGenerator />} />
          <Route path="/tools/meeting-notes" element={<MeetingNotes />} />
          <Route path="/tools/lecture-notes" element={<LectureNotes />} />
          {/* SEO Landing Pages */}
          <Route path="/tools/quizlet-alternative" element={<QuizletAlternative />} />
          <Route path="/tools/anki-alternative" element={<AnkiAlternative />} />
          <Route path="/tools/notability-alternative" element={<NotabilityAlternative />} />
          <Route path="/tools/otter-ai-alternative" element={<OtterAIAlternative />} />
          <Route path="/tools/chatgpt-for-students" element={<ChatGPTForStudents />} />
          <Route path="/tools/ai-study-tools" element={<AIStudyTools />} />
          <Route path="/tools/ai-flashcard-generator" element={<AIFlashcardGenerator />} />
          <Route path="/tools/lecture-transcription" element={<LectureTranscription />} />
          <Route path="/tools/pdf-to-flashcards" element={<PDFToFlashcards />} />
          <Route path="/tools/pdf-to-study-guide" element={<PDFToStudyGuide />} />
          <Route path="/tools/ai-note-taking" element={<AINoteTaking />} />
          <Route path="/compare/youwo-vs-chatgpt-for-studying" element={<YouWoVsChatGPTForStudying />} />
          {/* Internal tools - protected by query param */}
          <Route path="/internal/screenshots" element={<ProtectedInternalRoute><AppStoreScreenshots /></ProtectedInternalRoute>} />
          <Route path="/internal/landing-cards" element={<ProtectedInternalRoute><LandingCardRecorder /></ProtectedInternalRoute>} />
          <Route path="/internal/style-guide" element={<ProtectedInternalRoute><StyleGuide /></ProtectedInternalRoute>} />
          <Route path="/internal/design-playground" element={<ProtectedInternalRoute><DesignPlayground /></ProtectedInternalRoute>} />
          <Route path="/internal/youwo-consumer-landing" element={<ProtectedInternalRoute><YouWoConsumerLanding /></ProtectedInternalRoute>} />
          <Route path="/internal/download" element={<ProtectedInternalRoute><DownloadDesktop /></ProtectedInternalRoute>} />
          <Route path="/internal/competitors" element={<ProtectedInternalRoute><CompetitorAnalysis /></ProtectedInternalRoute>} />
          <Route path="/internal/dashboard" element={<ProtectedInternalRoute><InternalDashboard /></ProtectedInternalRoute>} />
          <Route
            path="/reset-password-email"
            element={<ResetPasswordEmail />}
          />
          <Route
            path="/email-verification"
            element={<EmailVerificationResult />}
          />
          <Route path="*" element={<Navigate to="landing-page" />} />
        </>
        // renderRoutesForRole(userRole)
      )}
    </Routes>
  );
}

injectStore(store);

const App = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
